/**
 * Detect a full-page reload loop and break it.
 *
 * Clerk redirects the browser when it cannot establish a session on an origin
 * it doesn't recognise — the page reloads, Clerk redirects again, and the tab
 * spins forever. From the outside that looks like "the site is broken" with
 * nothing to act on: no error, no console output that stays on screen long
 * enough to read.
 *
 * Counting real page loads (SPA route changes don't count) lets the app stop
 * mounting Clerk after a few and show what to check instead.
 */

const KEY = "home-load-trace";
/**
 * Wide enough to catch a slow loop. A cycle is a full page load plus a
 * redirect round-trip, which on a slow connection can take several seconds —
 * a 10s window never accumulated 5 of those, so the guard sat silent while
 * the tab visibly looped. Six full reloads inside a minute with no session
 * established is still not something a human refresh habit produces, and the
 * diagnostic page has a "try again" that clears the trace.
 */
const WINDOW_MS = 60_000;
const LIMIT = 6;

function read(): number[] {
  try {
    const raw = sessionStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === "number") : [];
  } catch {
    return [];
  }
}

/** Call once per page load, before anything mounts Clerk. */
export function recordLoad(): number {
  try {
    const now = Date.now();
    const recent = read().filter((t) => now - t < WINDOW_MS);
    recent.push(now);
    sessionStorage.setItem(KEY, JSON.stringify(recent));
    return recent.length;
  } catch {
    // Private mode / storage disabled: no loop detection, but nothing breaks.
    return 1;
  }
}

/** Cleared as soon as a session exists, so normal use never accumulates. */
export function clearLoadTrace(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do.
  }
}

export const LOAD_LIMIT = LIMIT;
